/**
 * CronAuthorPanel — cron management in Mission Control (ADR-094 ext): list all
 * crons, create a new one, edit an existing one (reuses the propose upsert), or
 * delete one (governed). MC live-validates (the same rules validate-crons.py
 * enforces) and builds the YAML deterministically, so a malformed cron file
 * can't be authored. Every change is a PR + in-app improvement — nothing runs or
 * stops until it's approved.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, AuthorTestsFailingError, type CronInput, type CronSummary, type FailingCheck, type ProposeSkillResult } from '../api/client'
import { FailingChecksPanel } from '../components/FailingChecksPanel'
import { DeletePanel } from '../components/DeletePanel'

function em(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

const SCHEDULE_PRESETS: { label: string; expr: string }[] = [
  { label: 'Daily 8 AM', expr: '0 8 * * *' },
  { label: 'Weekdays 8 AM', expr: '0 8 * * 1-5' },
  { label: 'Hourly', expr: '0 * * * *' },
  { label: 'Every 15 min', expr: '*/15 * * * *' },
]

const BLANK = {
  layer: 'agent' as 'agent' | 'fleet',
  name: '',
  schedule: '0 8 * * *',
  kind: 'activity' as const,
  activity: '',
  deliver: '',
}

export function CronAuthorPanel({
  canPropose,
  roster,
  scopedAgent,
  isCoreOwned,
  initialName,
  fleetSha,
  onClose,
}: {
  canPropose: boolean
  roster: string[]
  // ADR-111 IA-1: when set, the agent picker is locked to this agent and the
  // "Existing crons" list is filtered to just its crons.
  scopedAgent?: string
  // ADR-103: the scoped agent is core-owned, so NOTHING here is authorable.
  //
  // This is a distinct gate from `roster`, and the distinction is what was
  // missing. The panel already excludes core agents from the agent PICKER — but
  // in the Crons facet there is no picker: `scopedAgent` wins, and it is whichever
  // agent the rail selected, core or not. So a fleet owner on a core agent's Crons
  // facet got a fully live "New cron" form aimed at an agent the server refuses
  // (cron_authoring.go's denyCoreOwned), and learned that only by filling it in
  // and pressing Propose.
  isCoreOwned?: boolean
  // The cron named in the URL, opened for edit once the list loads. Only
  // meaningful alongside scopedAgent, which is what makes the name unambiguous —
  // see the URL-sync effect below.
  initialName?: string
  // ADR-101: the /fleet commit SHA (polled by the parent). When it changes,
  // published crons may have changed — refresh the list.
  fleetSha?: string
  // Optional: when omitted (e.g. embedded as the first-class Crons view) the
  // panel has no ✕ close — navigation is the studio tab switch.
  onClose?: () => void
}) {
  const navigate = useNavigate()
  const [crons, setCrons] = useState<CronSummary[]>([])
  const [loadingList, setLoadingList] = useState(true)
  const [mode, setMode] = useState<'new' | 'edit'>('new')

  const [layer, setLayer] = useState(BLANK.layer)
  // scopedAgent is authoritative the moment it's known (it comes from the
  // route/rail, not from `roster`, which may still be loading) — trust it
  // directly rather than waiting for roster to contain it.
  const [agent, setAgent] = useState(scopedAgent || (roster[0] ?? ''))
  const [name, setName] = useState(BLANK.name)
  const [schedule, setSchedule] = useState(BLANK.schedule)
  const [activity, setActivity] = useState(BLANK.activity)
  const [deliver, setDeliver] = useState(BLANK.deliver)
  const [note, setNote] = useState('')

  const [activityOptions, setActivityOptions] = useState<string[]>([])
  // Required inputs per workflow name. A cron supplies no values, so a workflow
  // that needs one can never run on a schedule — it fails inside
  // instantiateActivity BEFORE dispatch, which creates no conversation and no run
  // history, silently, on every tick. orion's hourly-triage was authored HERE and
  // did exactly that for a month, because this panel could not see that `triage`
  // needed a ticket_gid.
  const [requiredInputs, setRequiredInputs] = useState<Record<string, string[]>>({})
  const [cronInputs, setCronInputs] = useState<Record<string, string>>({})
  const [validation, setValidation] = useState<{ ok: boolean; errors: string[] } | null>(null)
  const [validating, setValidating] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // ADR-107 P3: the fleet's own author tests failed against this proposed
  // cron (409 author_tests_failing) — rendered via the SAME FailingChecksPanel
  // ADR-1027's PR review uses, not the generic err toast.
  const [failingChecks, setFailingChecks] = useState<FailingCheck[]>([])
  const [result, setResult] = useState<ProposeSkillResult | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  const loadList = () => {
    setLoadingList(true)
    api.listCrons()
      .then(r => setCrons(r.crons ?? []))
      .catch(e => setErr(em(e)))
      .finally(() => setLoadingList(false))
  }
  useEffect(loadList, [])

  const visibleCrons = useMemo(
    () => (scopedAgent ? crons.filter(c => c.agent === scopedAgent) : crons),
    [crons, scopedAgent],
  )

  // ADR-101: refresh the list when published content changes underneath.
  const firstSha = useRef(true)
  useEffect(() => {
    if (!fleetSha) return
    if (firstSha.current) { firstSha.current = false; return }
    loadList()
  }, [fleetSha])

  useEffect(() => {
    let live = true
    api.listActivities()
      .then(r => {
        if (!live) return
        const mine = r.activities.filter(a => a.agent === agent)
        setActivityOptions(mine.map(a => a.name).sort())
        setRequiredInputs(Object.fromEntries(
          mine.map(a => [a.name, a.required_inputs ?? []]),
        ))
      })
      .catch(() => { if (live) { setActivityOptions([]); setRequiredInputs({}) } })
    return () => { live = false }
  }, [agent])

  // What the selected workflow needs, and what is still unsupplied. The server
  // validates this too (ValidateCron) and its message is what gets shown; this
  // local copy exists so the field appears and submit is blocked WITHOUT waiting
  // on the 500ms debounce, not as a second source of truth.
  const needed = requiredInputs[activity.trim()] ?? []
  // EVERY non-empty entry, not just the required ones. cronInputs is hydrated
  // from the cron being edited, and an activity input that HAS a default is not
  // in `needed` — so filtering to `needed` would send only the required keys and
  // the writer, which rewrites `inputs:` wholesale, would delete a deliberate
  // optional override during an unrelated schedule edit.
  const suppliedInputs = Object.fromEntries(
    Object.entries(cronInputs).map(([k, v]) => [k, (v ?? '').trim()]).filter(([, v]) => v !== ''),
  ) as Record<string, string>
  // Hydrated keys the selected workflow does not REQUIRE — an optional override
  // already on this cron. Shown so it is editable rather than invisibly retained.
  const extraInputs = Object.keys(suppliedInputs).filter(k => !needed.includes(k))
  const unsuppliedInputs = needed.filter(n => !(n in suppliedInputs))

  const input: CronInput = {
    layer, agent, name: name.trim(), schedule: schedule.trim(), kind: BLANK.kind,
    activity: activity.trim(),
    deliver: deliver.trim() || undefined,
    inputs: Object.keys(suppliedInputs).length > 0 ? suppliedInputs : undefined,
  }

  useEffect(() => {
    if (!agent || !name.trim()) { setValidation(null); return }
    setValidating(true)
    const h = setTimeout(() => {
      api.validateCron(input).then(v => setValidation(v)).catch(e => setErr(em(e))).finally(() => setValidating(false))
    }, 500)
    return () => clearTimeout(h)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, agent, name, schedule, activity, deliver, JSON.stringify(cronInputs)])

  // --- Deep linking (ADR-111 IA-6) ------------------------------------------
  //
  // An operator shares a cron by copying the address bar, so the focused cron
  // has to BE in the address bar. Two halves, and both are needed: the URL is
  // rewritten as the operator moves (below), and a pasted URL resolves back to
  // the cron (the effect after it).
  //
  // Keyed on NAME alone, with no `layer` segment, because (agent, name) is
  // already unique: validate-crons.py merges the fleet and agent layers into one
  // `names_by_profile` set and reports a collision as "duplicate job name %r on
  // profile %r". A URL cannot address two crons, so it does not carry a
  // discriminator the fleet is not allowed to need.
  //
  // Only when scopedAgent is set. Unscoped, the panel lists every agent's crons
  // and the name is not unique across them, so there is no honest URL to write.
  const cronPath = (n?: string) =>
    `/fleet-editor/agents/${encodeURIComponent(scopedAgent ?? '')}/crons${n ? `/${encodeURIComponent(n)}` : ''}`
  // replace: focusing a cron is not a distinct "page", and pushing would make
  // browser Back walk the operator through every row they clicked.
  const syncURL = (n?: string) => {
    if (!scopedAgent) return
    navigate(cronPath(n), { replace: true })
  }

  const startNew = () => {
    setMode('new'); setName(BLANK.name); setSchedule(BLANK.schedule)
    setActivity(BLANK.activity); setDeliver(BLANK.deliver); setLayer(BLANK.layer)
    setNote(''); setValidation(null); setErr(null); setFailingChecks([])
    // Cleared, or a value typed for the LAST cron is silently reused by this
    // one — and for an input like ticket_gid that means scheduling work against
    // the wrong entity.
    setCronInputs({})
    syncURL(undefined)
  }
  const startEdit = (c: CronSummary) => {
    setMode('edit'); setLayer(c.layer); setAgent(c.agent); setName(c.name); setSchedule(c.schedule)
    setActivity(c.kind === 'prompt' ? '' : (c.activity ?? '')); setDeliver(c.deliver ?? '')
    // HYDRATED, not cleared. applyCronFields rewrites the `inputs:` mapping
    // wholesale so a dropped input actually drops, which means editing this
    // cron's schedule with an empty form would silently delete values it
    // already had.
    setCronInputs({ ...(c.inputs ?? {}) })
    setNote(''); setErr(null); setFailingChecks([]); setResult(null); setConfirmDelete(null)
    syncURL(c.name)
  }

  // Resolve a pasted /crons/:name once the list has loaded.
  //
  // Guarded by a ref rather than by comparing to current state: the operator must
  // stay free to click ＋ New or another cron afterwards without this snapping
  // them back to the link they arrived on. It fires for the FIRST list load only.
  //
  // A name that matches nothing is left alone deliberately — no redirect, no
  // error toast. The cron may be staged and not yet deployed, or the link may be
  // stale; silently rewriting the URL would destroy the evidence of which link
  // was shared, and an operator who pasted a link is better served by an empty
  // form and an intact address bar than by a bounce they cannot read.
  const resolvedDeepLink = useRef(false)
  useEffect(() => {
    if (resolvedDeepLink.current || loadingList || !initialName || !scopedAgent) return
    resolvedDeepLink.current = true
    const match = visibleCrons.find(c => c.name === initialName)
    if (match) startEdit(match)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingList, initialName, scopedAgent, visibleCrons])

  const valid = validation?.ok === true && unsuppliedInputs.length === 0
  const propose = async () => {
    setBusy(true); setErr(null); setFailingChecks([])
    try {
      setResult(await api.proposeCron(input, note.trim() || undefined))
      loadList(); startNew()
    } catch (e) {
      if (e instanceof AuthorTestsFailingError) setFailingChecks(e.checks)
      else setErr(em(e))
    } finally { setBusy(false) }
  }
  const doDelete = async (c: CronSummary) => {
    setBusy(true); setErr(null)
    try {
      setResult(await api.deleteCron({ layer: c.layer, agent: c.agent, name: c.name }))
      setConfirmDelete(null); loadList()
    } catch (e) { setErr(em(e)) } finally { setBusy(false) }
  }

  return (
    <div className="rounded-lg border border-purple-200 bg-purple-50/40 px-4 py-3 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-purple-800">Crons</h3>
        {onClose && <button type="button" onClick={onClose} className="text-xs text-gray-500 hover:text-gray-800">✕ close</button>}
      </div>

      {result && (
        result.improvement_warning ? (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-1">
            <p>{result.improvement_warning}</p>
          </div>
        ) : (
          <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 space-y-1">
            <p>Sent for review. The change takes effect only after you approve it (the list below still reflects what&apos;s live).</p>
            {result.improvement_id
              ? <Link to={`/improvements/${encodeURIComponent(result.improvement_id)}/pr`} className="font-medium underline">Review &amp; approve in Mission Control →</Link>
              : <span>Find it under <Link to="/improvements" className="font-medium underline">Improvements</Link>.</span>}
          </div>
        )
      )}

      {/* Existing crons */}
      <div className="rounded border border-gray-200 bg-white/70">
        <div className="px-2 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">Existing crons</div>
        {loadingList ? (
          <p className="text-xs text-gray-400 p-2 animate-pulse">Loading…</p>
        ) : visibleCrons.length === 0 ? (
          <p className="text-xs text-gray-500 p-2">No crons yet.</p>
        ) : (
          <ul className="divide-y divide-gray-50 max-h-52 overflow-auto">
            {visibleCrons.map(c => (
              <li key={`${c.layer}:${c.agent}:${c.name}`} className="flex flex-wrap items-center gap-2 px-2 py-1.5 text-xs">
                <div className="min-w-0 flex-1">
                  <span className="font-mono text-gray-800">{c.agent}/{c.name}</span>
                  <span className="ml-1.5 text-gray-400">{c.layer} · <span className="font-mono">{c.schedule}</span> · {c.kind === 'activity' ? `⚙ ${c.activity}` : 'prompt (retired)'}</span>
                </div>
                {c.core ? (
                  <span className="shrink-0 text-[10px] text-gray-400 italic">core-owned · read-only</span>
                ) : confirmDelete === `${c.layer}:${c.agent}:${c.name}` ? (
                  <span className="shrink-0 flex items-center gap-1">
                    <span className="text-red-600">delete?</span>
                    <button type="button" disabled={busy} onClick={() => void doDelete(c)} className="px-1.5 py-0.5 rounded bg-red-600 text-white disabled:opacity-50">yes</button>
                    <button type="button" onClick={() => setConfirmDelete(null)} className="px-1 text-gray-500 hover:text-gray-700">no</button>
                  </span>
                ) : (
                  <>
                    <button type="button" onClick={() => startEdit(c)} className="shrink-0 text-indigo-600 hover:text-indigo-800 underline">Edit</button>
                    {/* A direct child of the flex-wrap row, not of a shrink-0 cluster: opened,
                        the panel takes a full-width line of its own instead of pushing the
                        row past the card. */}
                    {/* ADR-123: a cron is a leaf (nothing points at one), so its
                        plan is small — but it goes through the same dependency-aware
                        panel as every other entity, so an operator never has two
                        deletes with different safety properties. */}
                    {/* The result is LIFTED, not left inside DeletePanel. Refreshing the list
                        swaps this whole subtree for the loading row, which unmounts the panel and
                        takes its result state with it -- including a registration warning the
                        operator has had no chance to read. The panel above outlives the refresh. */}
                    {canPropose && <DeletePanel target={{ kind: 'cron', agent: c.agent, name: c.name }} label="Delete" onProposed={r => { setResult(r); loadList() }} />}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* New / edit form — absent entirely for a core-owned agent (ADR-103).
          Not merely un-proposable: a form you can fill in and not submit teaches
          the rule at the END, after the work. The list above stays, because
          READING a core agent's crons is exactly what an operator needs to do
          before asking core to change one. */}
      {isCoreOwned ? (
        <p className="text-xs text-gray-500 border-t border-gray-100 pt-3">
          <span className="font-medium">{scopedAgent}</span> is core-owned (ADR-103) — its crons are
          managed in core so a fix reaches every fleet, and can&rsquo;t be authored per-fleet.
        </p>
      ) : (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold text-gray-500">{mode === 'edit' ? `Editing ${agent}/${name}` : 'New cron'}</div>
          {mode === 'edit' && <button type="button" onClick={startNew} className="text-[11px] px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">＋ New cron instead</button>}
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-gray-600 block">Layer
            <select value={layer} disabled={mode === 'edit'} onChange={e => setLayer(e.target.value as 'agent' | 'fleet')}
              className="mt-1 block text-sm border border-gray-300 rounded px-2 py-1 disabled:bg-gray-100 disabled:text-gray-500">
              <option value="agent">Agent</option>
              <option value="fleet">Fleet</option>
            </select>
          </label>
          <label className="text-xs text-gray-600 block">{layer === 'fleet' ? 'Profile (agent)' : 'Agent'}
            <select value={agent} disabled={mode === 'edit' || !!scopedAgent} onChange={e => setAgent(e.target.value)}
              className="mt-1 block text-sm border border-gray-300 rounded px-2 py-1 min-w-[10rem] disabled:bg-gray-100 disabled:text-gray-500">
              {roster.length === 0 && <option value="">(loading…)</option>}
              {(scopedAgent ? [scopedAgent] : roster).map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <label className="text-xs text-gray-600 block flex-1 min-w-[12rem]">Name
            <input type="text" value={name} disabled={mode === 'edit'} onChange={e => setName(e.target.value)}
              placeholder={layer === 'fleet' ? `${agent}-<job>` : 'e.g. morning-briefing'}
              className="mt-1 block w-full text-sm border border-gray-300 rounded px-2 py-1 disabled:bg-gray-100 disabled:text-gray-500" />
          </label>
        </div>

        <div>
          <label className="text-xs text-gray-600 block">Schedule (cron: min hour dom mon dow)
            <input type="text" value={schedule} onChange={e => setSchedule(e.target.value)}
              className="mt-1 block w-full text-sm font-mono border border-gray-300 rounded px-2 py-1" />
          </label>
          <div className="mt-1 flex flex-wrap gap-1">
            {SCHEDULE_PRESETS.map(p => (
              <button key={p.expr} type="button" onClick={() => setSchedule(p.expr)}
                className="text-[11px] px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">{p.label}</button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-xs text-gray-600 block">Workflow
            <select value={activity} onChange={e => { setActivity(e.target.value); setCronInputs({}) }}
              className="mt-1 block text-sm border border-gray-300 rounded px-2 py-1 min-w-[16rem]">
              <option value="">— pick a workflow —</option>
              {activityOptions.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            {activityOptions.length === 0 && <span className="ml-2 text-[11px] text-gray-400">{agent} has no workflows yet.</span>}
            {(needed.length > 0 || extraInputs.length > 0) && (
              <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2">
                <p className="text-[11px] text-amber-900">
                  <strong>{activity.trim()}</strong> needs {needed.length === 1 ? 'an input' : 'inputs'} a
                  schedule cannot provide. A cron passes no values, so this workflow would fail before it
                  ever ran. Usually the right fix is a <em>sweep</em>: a workflow that takes no arguments,
                  finds the items to act on, and calls this one for each. Supply a fixed value below only
                  if the same one always applies.
                </p>
                {[...needed, ...extraInputs].map(n => (
                  <label key={n} className="mt-1.5 block text-[11px] text-amber-900">
                    <span className="font-mono">{n}</span>{' '}
                    <span className="text-amber-700">{needed.includes(n) ? '(required)' : '(optional — already set)'}</span>
                    <input type="text" value={cronInputs[n] ?? ''}
                      onChange={e => setCronInputs({ ...cronInputs, [n]: e.target.value })}
                      placeholder="a literal value — not {{...}}"
                      className="mt-0.5 block w-full text-xs font-mono border border-amber-300 rounded px-2 py-1" />
                  </label>
                ))}
              </div>
            )}
          </label>
        </div>

        <label className="text-xs text-gray-600 block">Deliver to (optional channel)
          <input type="text" value={deliver} onChange={e => setDeliver(e.target.value)}
            placeholder="e.g. slack:C123 (must be in the fleet's allowlist, if defined)"
            className="mt-1 block w-full text-sm border border-gray-300 rounded px-2 py-1" />
        </label>

        {validating && <p className="text-[11px] text-gray-400 animate-pulse">validating…</p>}
        {validation && (
          validation.ok
            ? <p className="text-xs text-green-700">✓ valid</p>
            : <ul className="text-xs text-red-700 list-disc list-inside">{validation.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        )}
        {err && <p className="text-xs text-red-600 whitespace-pre-wrap break-words">{err}</p>}
        {failingChecks.length > 0 && <FailingChecksPanel checks={failingChecks} />}

        <div className="flex items-center gap-2">
          {canPropose ? (
            <button type="button" disabled={busy || validating || !valid} onClick={() => void propose()}
              className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
              title={!valid ? 'The cron must pass validation before it can be proposed' : undefined}>
              {busy ? 'Running checks…' : mode === 'edit' ? 'Propose changes' : 'Propose cron'}
            </button>
          ) : <span className="text-xs text-gray-500">Only a fleet owner can propose a cron.</span>}
          <input type="text" value={note} onChange={e => setNote(e.target.value)}
            placeholder="Optional note for the reviewer" className="flex-1 text-xs border border-gray-200 rounded px-2 py-1" />
        </div>
      </div>
      )}
    </div>
  )
}
